import { Controller } from "@hotwired/stimulus"
import BaseGrid, { html, Handlebars } from "../utils/base_grid"

// Pre-compile templates
const actionButtonsTemplate = Handlebars.compile(`
  <div class="btn-group">
    <a href="{{viewUrl}}" class="btn btn-icon btn-sm" data-turbo-frame="_top" data-bs-toggle="tooltip" data-bs-title="{{viewTitle}}">
      <i class="fas fa-eye"></i>
    </a>
    <a href="{{editUrl}}" class="btn btn-icon btn-sm" data-turbo-frame="_top" data-bs-toggle="tooltip" data-bs-title="{{editTitle}}">
      <i class="fas fa-edit"></i>
    </a>
  </div>
`)

const nameLinkTemplate = Handlebars.compile(`
  <a href="{{viewUrl}}" class="text-decoration-none fw-bold" data-turbo-frame="_top">{{name}}</a>
`)

const typeBadgeTemplate = Handlebars.compile(`
  <div style="display: flex; align-items: center; gap: 0.5rem;">
    <span class="badge badge-type">{{type}}</span>
  </div>
`)

const statusBadgeTemplate = Handlebars.compile(`
  <span class="badge badge-{{status}}">{{status}}</span>
`)

const languageBadgeTemplate = Handlebars.compile(`
  <span class="badge badge-{{language}}">{{language}}</span>
`)

const checkboxTemplate = Handlebars.compile(`
  <input type="checkbox" value="{{id}}" class="form-check-input grid-checkbox" data-action="change->grid#toggleSelection">
`)

export default class extends Controller {
  static values = { 
    url: String,
    translations: Object
  }
  static targets = ["selectedIds", "container", "selectedCount", "bulkActions", "skeleton"]

  connect() {
    // Get translations with defaults
    const t = this.translationsValue || {}
    const labels = {
      name: t.name || "Name",
      type: t.type || "Type",
      language: t.language || "Language",
      status: t.status || "Status",
      actions: t.actions || "Actions",
      view: t.view || "View",
      edit: t.edit || "Edit"
    }
    
    // Store type translations for later use
    this.typeTranslations = t.types || {}
    this.labels = labels

    this.baseGrid = new BaseGrid(this, {
      limit: 25,
      columns: [
        { 
          id: "select", 
          name: html('<input type="checkbox" class="form-check-input" data-action="change->grid#toggleAll">'),
          sort: false,
          width: '50px',
          formatter: (cell, row) => html(checkboxTemplate({ id: row.cells[5].data })) // ID is at index 5
        },
        { 
          name: labels.name, 
          id: "name",
          formatter: (cell, row) => html(nameLinkTemplate({ name: cell, viewUrl: row.cells[6].data }))
        },
        { 
          name: labels.type, 
          id: "type",
          formatter: (cell) => {
            const translatedType = this.typeTranslations[cell] || cell
            return html(typeBadgeTemplate({ type: translatedType }))
          }
        },
        { 
          name: labels.language, 
          id: "language",
          formatter: (cell) => html(languageBadgeTemplate({ language: cell.toUpperCase() }))
        },
        { 
          name: labels.status, 
          id: "status",
          formatter: (cell) => html(statusBadgeTemplate({ status: cell }))
        },
        { name: "ID", id: "id", hidden: true },
        { name: "ViewURL", id: "view_url", hidden: true },
        { name: "EditURL", id: "edit_url", hidden: true },
        { 
          name: labels.actions,
          sort: false,
          formatter: (cell, row) => {
             return html(actionButtonsTemplate({ 
               viewUrl: row.cells[6].data, 
               editUrl: row.cells[7].data,
               viewTitle: this.labels.view,
               editTitle: this.labels.edit
             })) 
          }
        }
      ],
      mapData: (item) => [
        null, // Placeholder for select column
        item.name, 
        item.type, 
        item.language, 
        item.status, 
        item.id, 
        item.view_url,
        item.edit_url
      ]
    })

    this.grid = this.baseGrid.create()

    // Listen for external filter changes
    this.filterHandler = this.handleFilterChange.bind(this)
    window.addEventListener("filter:change", this.filterHandler)
  }

  disconnect() {
    if (this.filterHandler) {
      window.removeEventListener("filter:change", this.filterHandler)
    }
    this.baseGrid.destroy()
  }

  handleFilterChange(event) {
    const { url } = event.detail
    
    // Update grid config with new server URL
    // The event url might be HTML path, so we ensure format=json
    const gridUrl = new URL(url)
    gridUrl.searchParams.set("format", "json")
    
    this.baseGrid.updateUrl(gridUrl.toString())
  }

  toggleSelection(event) {
    this.updateSelectedIds()
  }

  toggleAll(event) {
    const checkboxes = this.containerTarget.querySelectorAll('.grid-checkbox')
    checkboxes.forEach(cb => {
      cb.checked = event.target.checked
    })
    this.updateSelectedIds()
  }

  updateSelectedIds() {
    const checkboxes = this.containerTarget.querySelectorAll('.grid-checkbox:checked')
    const ids = Array.from(checkboxes).map(cb => cb.value)
    
    if (this.hasSelectedIdsTarget) {
      this.selectedIdsTarget.value = ids.join(",")
    }

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = ids.length
    }

    if (this.hasBulkActionsTarget) {
      if (ids.length > 0) {
        this.bulkActionsTarget.style.display = "flex"
      } else {
        this.bulkActionsTarget.style.display = "none"
      }
    }
  }

  deleteSelected() {
    if (confirm("Are you sure you want to delete the selected items?")) {
      this.performBulkAction("/admin/contents/bulk_destroy", "DELETE")
    }
  }

  publishSelected() {
    this.performBulkAction("/admin/contents/bulk_publish", "POST")
  }

  draftSelected() {
    this.performBulkAction("/admin/contents/bulk_draft", "POST")
  }

  performBulkAction(url, method) {
    const ids = this.selectedIdsTarget.value.split(",")
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    fetch(url, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ ids: ids })
    })
    .then(response => {
      if (response.ok) {
        // Refresh grid
        this.grid.forceRender()
        // Reset selection
        this.selectedIdsTarget.value = ""
        this.updateSelectedIds()
        // Uncheck all checkboxes
        this.containerTarget.querySelectorAll('.grid-checkbox').forEach(cb => cb.checked = false)
        this.containerTarget.querySelector('input[data-action="change->grid#toggleAll"]').checked = false
      } else {
        alert("Action failed")
      }
    })
  }
}
